import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useToast } from "../App";
import CardItem from "../components/CardItem";
import ChatPanel from "../components/ChatPanel";
import PipelineNav from "../components/PipelineNav";
import { api } from "../lib/api";
import type { Card, CardStatus, CardType, MediaAsset, ProjectDetail } from "../lib/types";

type Filter = "review" | "flagged" | "accepted" | "rejected" | "duplicate" | "all";

const FILTERS: { key: Filter; label: string; statuses?: CardStatus[] }[] = [
  { key: "review", label: "To review", statuses: ["proposed", "flagged"] },
  { key: "flagged", label: "Flagged", statuses: ["flagged"] },
  { key: "accepted", label: "Accepted", statuses: ["accepted"] },
  { key: "duplicate", label: "Duplicates", statuses: ["duplicate"] },
  { key: "rejected", label: "Rejected", statuses: ["rejected"] },
  { key: "all", label: "Everything" },
];

export default function ReviewPage() {
  const { projectId = "" } = useParams();
  const { pushError, push } = useToast();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [media, setMedia] = useState<Map<string, MediaAsset>>(new Map());
  const [filter, setFilter] = useState<Filter>("review");
  const [typeFilter, setTypeFilter] = useState<CardType | "">("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [detail, allCards, assets] = await Promise.all([
        api.getProject(projectId),
        api.listCards(projectId, { order: "review" }),
        api.listMedia(projectId),
      ]);
      setProject(detail);
      setCards(allCards);
      setMedia(new Map(assets.map((a) => [a.id, a])));
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
  }, [projectId, pushError]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const byStatus = new Map<string, number>();
    cards.forEach((c) => byStatus.set(c.status, (byStatus.get(c.status) ?? 0) + 1));
    return {
      review: (byStatus.get("proposed") ?? 0) + (byStatus.get("flagged") ?? 0),
      flagged: byStatus.get("flagged") ?? 0,
      accepted: byStatus.get("accepted") ?? 0,
      rejected: byStatus.get("rejected") ?? 0,
      duplicate: byStatus.get("duplicate") ?? 0,
      all: cards.length,
    } as Record<Filter, number>;
  }, [cards]);

  const visible = useMemo(() => {
    const entry = FILTERS.find((f) => f.key === filter)!;
    const needle = search.trim().toLowerCase();
    return cards.filter((card) => {
      if (entry.statuses && !entry.statuses.includes(card.status)) return false;
      if (typeFilter && card.type !== typeFilter) return false;
      if (needle) {
        const haystack = `${card.front} ${card.back} ${card.cloze_text} ${card.header} ${(
          card.tags ?? []
        ).join(" ")}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [cards, filter, typeFilter, search]);

  const cardsById = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);

  const replace = useCallback((updated: Card) => {
    setCards((current) => current.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const removeCard = useCallback((id: string) => {
    setCards((current) => current.filter((c) => c.id !== id));
    setSelected((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const bulk = async (status: CardStatus) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const result = await api.bulkStatus(projectId, ids, status);
      result.blocked.forEach((b) => push(`Could not accept a card: ${b.reason}`, "error"));
      setSelected(new Set());
      await load();
    } catch (error) {
      pushError(error);
    }
  };

  const acceptAllPassing = async () => {
    try {
      const { accepted } = await api.acceptAllPassing(projectId);
      push(`Accepted ${accepted} cards that passed every check.`, "good");
      await load();
    } catch (error) {
      pushError(error);
    }
  };

  const onRefined = async (changedIds: string[]) => {
    await load();
    setHighlighted(new Set(changedIds));
    window.setTimeout(() => setHighlighted(new Set()), 2500);
  };

  if (loading) return <main className="page"><p className="muted">Loading cards…</p></main>;

  return (
    <main className="page page-wide">
      <PipelineNav projectId={projectId} projectName={project?.name ?? "Project"} current="review" />

      <div className="page-head">
        <div className="grow">
          <h1>Review cards</h1>
          <p>
            Nothing is exported until you accept it. Flagged cards are at the top — those are the
            ones the checks were unsure about.
          </p>
        </div>
        <Link to={`/projects/${projectId}/export`} className="btn btn-primary btn-lg">
          Export to Anki →
        </Link>
      </div>

      <div className="review-layout">
        <aside className="sidebar">
          <div className="panel">
            <div className="filter-group">
              {FILTERS.map((entry) => (
                <button
                  key={entry.key}
                  className="filter-btn"
                  aria-pressed={filter === entry.key}
                  onClick={() => setFilter(entry.key)}
                >
                  <span>{entry.label}</span>
                  <span className="filter-count">{counts[entry.key] ?? 0}</span>
                </button>
              ))}
            </div>

            <hr className="divider" />

            <label className="field" style={{ margin: 0 }}>
              <span className="label">Search</span>
              <input
                type="search"
                value={search}
                placeholder="Find a card…"
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            <label className="field" style={{ marginTop: 12, marginBottom: 0 }}>
              <span className="label">Card type</span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as CardType | "")}
              >
                <option value="">All types</option>
                <option value="basic">Basic</option>
                <option value="basic_reversed">Basic + reversed</option>
                <option value="cloze">Cloze</option>
                <option value="image_occlusion">Image occlusion</option>
              </select>
            </label>
          </div>

          <div className="panel">
            <h3 style={{ marginBottom: 10 }}>Bulk actions</h3>
            {selected.size > 0 ? (
              <div className="stack" style={{ gap: 6 }}>
                <span className="small muted">{selected.size} selected</span>
                <button className="btn-sm btn-good" onClick={() => bulk("accepted")}>
                  Accept selected
                </button>
                <button className="btn-sm btn-bad" onClick={() => bulk("rejected")}>
                  Reject selected
                </button>
                <button className="btn-sm btn-ghost" onClick={() => setSelected(new Set())}>
                  Clear selection
                </button>
              </div>
            ) : (
              <div className="stack" style={{ gap: 6 }}>
                <button
                  className="btn-sm"
                  onClick={() => setSelected(new Set(visible.map((c) => c.id)))}
                  disabled={!visible.length}
                >
                  Select all shown ({visible.length})
                </button>
                <button className="btn-sm btn-good" onClick={acceptAllPassing}>
                  Accept everything that passed
                </button>
                <span className="small faint">
                  Skips flagged cards and anything with an error.
                </span>
              </div>
            )}
          </div>

          <ChatPanel projectId={projectId} selectedIds={[...selected]} onApplied={onRefined} />
        </aside>

        <div>
          {visible.length === 0 ? (
            <div className="empty">
              <h3>Nothing here</h3>
              <p className="muted">
                {cards.length === 0
                  ? "No cards yet. Generate some from the project page."
                  : "No cards match this filter."}
              </p>
              {cards.length === 0 && (
                <Link to={`/projects/${projectId}`} className="btn btn-primary">
                  Back to the project
                </Link>
              )}
            </div>
          ) : (
            <div className="card-list">
              {visible.map((card) => (
                <CardItem
                  key={card.id}
                  card={card}
                  media={media}
                  cardsById={cardsById}
                  selected={selected.has(card.id)}
                  highlighted={highlighted.has(card.id)}
                  onToggleSelect={toggleSelect}
                  onChanged={replace}
                  onRemoved={removeCard}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
